// components/Navbar.jsx
// components/Navbar.jsx

import React, { useState, useEffect } from "react";
import { Navbar as BootstrapNavbar, Container, Nav } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from "firebase/auth";
import { auth } from "../firebase";
import { getUserByUid } from "../services/authService";
import ThemeToggle from "./ThemeToggle";
import ThemeLogo from "./ThemeLogo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle user state changes
  function handleAuthStateChanged(user) {
    setIsAuthenticated(!!user);
    if (initializing) {
      setInitializing(false);
    }
    
    if (user) {
      // Fetch user data from Firestore
      getUserByUid(user.uid)
        .then((data) => {
          setUserData(data);
        })
        .catch((error) => {
          console.error('Error fetching user data:', error);
        });
    } else {
      setUserData(null);
    }
  }

  useEffect(() => {
    // Set Firebase Auth persistence to local
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        console.log('Firebase Auth persistence set to local');
      })
      .catch((error) => {
        console.error('Error setting persistence:', error);
      });

    // Subscribe to auth state changes
    const subscriber = onAuthStateChanged(auth, handleAuthStateChanged);
    return subscriber; // unsubscribe on unmount
  }, [initializing]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavClick = (e, targetId) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/");
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(targetId.replace("#", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          window.history.pushState(null, null, targetId);
        }
      }, 100);
      return;
    }

    // If we're on the home page, handle normal scroll behavior
    if (targetId.startsWith("/#")) {
      const id = targetId.split("#")[1];
      const element = document.getElementById(id);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: "smooth" });
        // Update URL hash without causing a page jump
        window.history.pushState(null, null, `#${id}`);
      }
    } else if (targetId.startsWith("#")) {
      const id = targetId.substring(1);
      const element = document.getElementById(id);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, null, `#${id}`);
      }
    }
  };

  const handleHomeClick = (e) => {
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/");
    } else {
      handleNavClick(e, "#home");
    }
  };

  return (
    <BootstrapNavbar
      expand="lg"
      fixed="top"
      className={`main-navbar ${scrolled ? "scrolled" : ""}`}
    >
      <Container>
        <BootstrapNavbar.Brand href="#home" onClick={handleHomeClick}>
          <ThemeLogo height="80" className="animate-float" />
        </BootstrapNavbar.Brand>
        <BootstrapNavbar.Toggle aria-controls="navbar-nav" />
        <BootstrapNavbar.Collapse id="navbar-nav">
          <Nav className="ms-auto align-items-center">
            <Nav.Link href="#home" onClick={handleHomeClick} className="nav-link-aiwave">
              Home
            </Nav.Link>
            <Nav.Link
              href="#features"
              onClick={(e) => handleNavClick(e, "#features")}
              className="nav-link-aiwave"
            >
              Features
            </Nav.Link>
            <Nav.Link
              href="#how-it-works"
              onClick={(e) => handleNavClick(e, "#how-it-works")}
              className="nav-link-aiwave"
            >
              How It Works
            </Nav.Link>
            <Nav.Link
              href="#pricing"
              onClick={(e) => handleNavClick(e, "#pricing")}
              className="nav-link-aiwave"
            >
              Pricing
            </Nav.Link>
            <Nav.Link
              href="#downloads"
              onClick={(e) => handleNavClick(e, "#downloads")}
              className="nav-link-aiwave"
            >
              Download
            </Nav.Link>
            <Nav.Link
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigate('/contact');
              }}
              className="nav-link-aiwave"
            >
              Contact
            </Nav.Link>
            
            {/* Theme Toggle */}
            <div className="ms-2 me-2">
              <ThemeToggle />
            </div>
            
            {!initializing && (
              isAuthenticated ? (
                <>
                  <Nav.Link 
                    as={Link} 
                    to="/profile" 
                    className="btn btn-success ms-2 btn-aiwave-primary"
                    style={{ 
                      minWidth: 'auto', 
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, var(--brand-start, #7a3cff), var(--brand-end, #4ea1ff))',
                      border: 'none',
                      color: 'white !important',
                      fontWeight: '600',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(122, 60, 255, 0.25)'
                    }}
                  >
                    {userData?.firstName || 'Profile'}
                  </Nav.Link>
                  <Nav.Link 
                    onClick={handleLogout}
                    className="btn btn-danger ms-2 btn-aiwave-primary"
                    style={{ 
                      minWidth: 'auto', 
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, var(--brand-start, #7a3cff), var(--brand-end, #4ea1ff))',
                      border: 'none',
                      color: 'white !important',
                      fontWeight: '600',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(122, 60, 255, 0.25)'
                    }}
                  >
                    Logout
                  </Nav.Link>
                </>
              ) : (
                <>
                  <Nav.Link
                    href="#footer"
                    className="btn btn-warning ms-2 btn-aiwave-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/register');
                    }}
                    style={{ 
                      background: 'linear-gradient(135deg, var(--brand-start, #7a3cff), var(--brand-end, #4ea1ff))',
                      border: 'none',
                      color: 'white !important',
                      fontWeight: '600',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(122, 60, 255, 0.25)'
                    }}
                  >
                    Register Now
                  </Nav.Link>
                  <Nav.Link 
                    as={Link} 
                    to="/login" 
                    className="btn btn-success ms-2 btn-aiwave-primary"
                    style={{ 
                      background: 'linear-gradient(135deg, var(--brand-start, #7a3cff), var(--brand-end, #4ea1ff))',
                      border: 'none',
                      color: 'white !important',
                      fontWeight: '600',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(122, 60, 255, 0.25)'
                    }}
                  >
                    Login
                  </Nav.Link>
                </>
              )
            )}
          </Nav>
        </BootstrapNavbar.Collapse>
      </Container>
    </BootstrapNavbar>
  );
}
